import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarIcon,
  DollarSign,
  CalendarClock,
  Trophy,
  Users,
  RefreshCw,
  Plus,
} from 'lucide-react';
import { contractService } from './_shared/api';
import type { ContractStats, ContractType, SellerStats } from './_shared/types';
import ContractTypeModal from './_components/ContractTypeModal';
import ContractFormModal from './_components/ContractFormModal';

/**
 * Prospect > Dashboard — visão de gestão pra admin SAAS.
 *
 * Diferenças vs apps/prospect-web/src/pages/Dashboard.tsx:
 * - Mantém botão "Novo Contrato" (admin SAAS pode lançar contratos)
 * - Stats pessoais (myStats) ficam zero pra admin SAAS — escondemos a
 *   seção quando vazia.
 * - Lista de vendedores é o foco principal, com link pra Launched
 *   filtrado por vendedorId.
 */
// Ouro/prata/bronze: cor de medalha, não token de tema — não segue o design system.
const PODIUM = ['#facc15', '#94a3b8', '#fb923c'];

export default function ProspectDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<ContractStats | null>(null);
  const [sellers, setSellers] = useState<SellerStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [formType, setFormType] = useState<ContractType | null>(null);

  async function load(): Promise<void> {
    setLoading(true);
    try {
      // teamStats = TODAS as vendas (mesma visão do supervisor no app);
      // myStats fica como fallback pra backend antigo sem /stats/team
      const [team, sellersList] = await Promise.all([
        contractService
          .teamStats()
          .catch(() => contractService.myStats().catch(() => null)),
        contractService.sellersStats().catch(() => [] as SellerStats[]),
      ]);
      setStats(team);
      setSellers(sellersList);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-fg">Prospect — Dashboard</h1>
          <p className="text-sm text-fg-2">Resumo dos lançamentos e vendedores</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowTypeModal(true)}
            className="h-10 px-4 bg-primary-solid hover:bg-primary-solid-hover rounded-xl text-white font-medium flex items-center gap-2 transition"
          >
            <Plus size={18} />
            <span className="hidden sm:inline">Novo contrato</span>
            <span className="sm:hidden">Novo</span>
          </button>
          <button
            onClick={() => void load()}
            disabled={loading}
            className="w-10 h-10 rounded-full bg-canvas hover:bg-hover border border-line flex items-center justify-center transition"
            aria-label="Atualizar"
          >
            <RefreshCw
              size={18}
              className={loading ? 'animate-spin text-primary' : 'text-fg-2'}
            />
          </button>
        </div>
      </header>

      {/* Stats do TIME — todas as vendas (visão supervisor do app) */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard
            icon={<CalendarIcon size={14} className="text-primary" />}
            label="Hoje"
            value={stats.launchedToday}
          />
          <StatCard
            icon={<CalendarClock size={14} className="text-violet-400" />}
            label="Este mês"
            value={stats.launchedThisMonth}
          />
          <StatCard
            icon={<DollarSign size={14} className="text-success" />}
            label="Ticket médio"
            value={`R$ ${stats.averageTicket.toFixed(2)}`}
            small
          />
          <StatCard
            icon={<Users size={14} className="text-warning" />}
            label="Total de contratos"
            value={stats.totalContracts}
          />
        </div>
      )}

      {/* Top 3 planos */}
      {stats && stats.topPlans.length > 0 && (
        <div className="bg-surface border border-line rounded-2xl p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Trophy size={14} className="text-primary" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-primary">
              Top 3 pacotes
            </h2>
          </div>
          <div className="divide-y divide-line">
            {stats.topPlans.map((p, i) => (
              <div key={p.erpId} className="flex items-center py-2.5">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center mr-3"
                  style={{ backgroundColor: `${PODIUM[i]}30`, color: PODIUM[i] }}
                >
                  <span className="text-xs font-bold">{i + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-fg truncate">{p.descricao}</div>
                  <div className="text-xs text-muted">
                    R$ {p.valor.toFixed(2)} · plano #{p.erpId}
                  </div>
                </div>
                <span className="bg-hover text-fg text-xs font-bold rounded-full px-2 py-0.5">
                  {p.count}x
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lista de vendedores */}
      <div>
        <div className="flex items-center gap-2 mb-3 px-1">
          <Users size={14} className="text-primary" />
          <h2 className="text-xs font-bold uppercase tracking-wider text-primary">
            Vendedores ({sellers.length})
          </h2>
        </div>

        {loading && sellers.length === 0 ? (
          <div className="bg-surface border border-line rounded-2xl p-6 text-center text-muted">
            Carregando…
          </div>
        ) : sellers.length === 0 ? (
          <div className="bg-surface border border-line rounded-2xl p-6 text-center text-muted text-sm">
            Sem dados de vendedores ainda.
          </div>
        ) : (
          <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
            {sellers.map((s) => (
              <button
                key={s.vendedorId}
                onClick={() => navigate(`/prospect/launched?vendedorId=${s.vendedorId}`)}
                className="text-left bg-surface border border-line hover:border-primary-solid rounded-2xl p-4 transition"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0 flex-1 pr-3">
                    <div className="text-sm font-semibold text-fg truncate">
                      {s.vendedorNome}
                    </div>
                    <div className="text-[11px] text-muted">ID #{s.vendedorId}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-success">{s.launchedThisMonth}</div>
                    <div className="text-[10px] uppercase text-muted">no mês</div>
                  </div>
                </div>
                <div className="flex gap-2 mb-2 text-xs">
                  <div className="flex-1 bg-canvas rounded-lg px-2 py-1.5">
                    <div className="text-[10px] uppercase text-muted">Hoje</div>
                    <div className="text-fg font-bold">{s.launchedToday}</div>
                  </div>
                  <div className="flex-1 bg-canvas rounded-lg px-2 py-1.5">
                    <div className="text-[10px] uppercase text-muted">Ticket</div>
                    <div className="text-fg font-bold">R$ {s.averageTicket.toFixed(0)}</div>
                  </div>
                  <div className="flex-1 bg-canvas rounded-lg px-2 py-1.5">
                    <div className="text-[10px] uppercase text-muted">Fat. mês</div>
                    <div className="text-warning font-bold">
                      R$ {(s.revenueThisMonth ?? 0).toFixed(0)}
                    </div>
                  </div>
                </div>
                {s.topPlan && (
                  <div className="bg-canvas rounded-lg px-2 py-1.5 flex items-center gap-2">
                    <Trophy size={10} className="text-warning" />
                    <span className="text-[11px] text-fg-2 flex-1 truncate">
                      <span className="text-warning font-medium">Top: </span>
                      {s.topPlan.descricao}
                    </span>
                    <span className="text-fg text-[10px] font-bold bg-hover px-1.5 py-0.5 rounded">
                      {s.topPlan.count}x
                    </span>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Modais de criação de contrato */}
      <ContractTypeModal
        open={showTypeModal}
        onClose={() => setShowTypeModal(false)}
        onSelect={(t) => {
          setShowTypeModal(false);
          setFormType(t);
        }}
      />
      {formType && (
        <ContractFormModal
          type={formType}
          onClose={() => setFormType(null)}
          onSuccess={() => {
            setFormType(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  small,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  small?: boolean;
}) {
  return (
    <div className="bg-surface border border-line rounded-2xl p-4">
      <div className="flex items-center gap-1.5 mb-1.5">
        {icon}
        <span className="text-[11px] font-medium uppercase text-fg-2">{label}</span>
      </div>
      <div className={`text-fg font-bold ${small ? 'text-base' : 'text-2xl'}`}>{value}</div>
    </div>
  );
}
